import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ddd;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #333;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : props.success ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : props.danger ? '#c82333' : props.success ? '#218838' : '#5a6268'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.color || '#333'};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.25rem;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  flex: 1;
  max-width: 300px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const BatchActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 1rem;
  background: #e7f3ff;
  border-radius: 8px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SelectedCount = styled.span`
  font-weight: 500;
  color: #007bff;
  margin-right: 1rem;
`;

const UserTable = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 80px 120px 100px 80px 100px 100px 150px 1fr;
  gap: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
  font-weight: 600;
  color: #495057;
  align-items: center;
`;

const UserRow = styled.div`
  display: grid;
  grid-template-columns: 50px 80px 120px 100px 80px 100px 100px 150px 1fr;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #eee;
  align-items: center;
  background: ${props => props.selected ? '#e7f3ff' : 'white'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.selected ? '#e7f3ff' : '#f9f9f9'};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
`;

const Username = styled.div`
  font-weight: 500;
  color: #333;
`;

const Nickname = styled.div`
  color: #666;
`;

const Role = styled.div`
  color: ${props => props.role === 'admin' ? '#dc3545' : '#007bff'};
  font-weight: 500;
`;

const Status = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background-color: ${props => props.active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.active ? '#155724' : '#721c24'};
`;

const OnlineStatus = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background-color: ${props => props.online ? '#d4edda' : '#e9ecef'};
  color: ${props => props.online ? '#155724' : '#495057'};
`;

const CreatedAt = styled.div`
  color: #888;
  font-size: 0.875rem;
`;

const UserActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 0.375rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background-color: #f8f9fa;
  }

  &.primary {
    background-color: #007bff;
    color: white;
    border-color: #007bff;

    &:hover {
      background-color: #0069d9;
    }
  }

  &.success {
    background-color: #28a745;
    color: white;
    border-color: #28a745;

    &:hover {
      background-color: #218838;
    }
  }

  &.danger {
    background-color: #dc3545;
    color: white;
    border-color: #dc3545;

    &:hover {
      background-color: #c82333;
    }
  }

  &.warning {
    background-color: #ffc107;
    color: #333;
    border-color: #ffc107;

    &:hover {
      background-color: #e0a800;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #888;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #dc3545;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;

  &:hover {
    background-color: #f8f9fa;
  }

  &.active {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;

  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background-color: #f8f9fa;
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Textarea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const DetailInfo = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 1rem;
`;

const DetailLabel = styled.div`
  font-weight: 500;
  color: #666;
`;

const DetailValue = styled.div`
  color: #333;
`;

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 2rem;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  z-index: 2000;
  animation: slideIn 0.3s ease;
  background-color: ${props => props.type === 'success' ? '#28a745' : props.type === 'error' ? '#dc3545' : '#ffc107'};
`;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState(null);
  
  const [detailModal, setDetailModal] = useState(null);
  const [editModal, setEditModal] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, pageSize: 20 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await axios.get('/api/admin/users', { params });
      if (response.data.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.totalPages);
      }
    } catch (err) {
      console.error('获取用户列表失败:', err);
      setError(err.response?.data?.message || '获取用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/users/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('获取统计失败:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(users.map(u => u._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const handleViewDetail = async (user) => {
    try {
      const response = await axios.get(`/api/admin/users/${user._id}`);
      if (response.data.success) {
        setDetailModal(response.data.user);
      }
    } catch (err) {
      showToast('获取用户详情失败', 'error');
    }
  };

  const handleEdit = (user) => {
    setEditModal({ ...user });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`/api/admin/users/${editModal._id}`, editModal);
      if (response.data.success) {
        showToast('用户信息更新成功');
        setEditModal(null);
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      showToast(err.response?.data?.message || '更新失败', 'error');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    if (!window.confirm(isActive ? '确定要禁用该用户吗？' : '确定要启用该用户吗？')) {
      return;
    }
    try {
      const response = await axios.put(`/api/admin/users/${userId}/status`, { isActive: !isActive });
      if (response.data.success) {
        showToast(response.data.message);
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      showToast('操作失败', 'error');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('确定要删除该用户吗？此操作不可恢复！')) {
      return;
    }
    try {
      const response = await axios.delete(`/api/admin/users/${userId}`);
      if (response.data.success) {
        showToast('用户删除成功');
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      showToast('删除失败', 'error');
    }
  };

  const handleBatchEnable = async () => {
    if (!window.confirm(`确定要启用 ${selectedUsers.length} 个用户吗？`)) {
      return;
    }
    try {
      const response = await axios.put('/api/admin/users/batch-status', {
        ids: selectedUsers,
        isActive: true
      });
      if (response.data.success) {
        showToast(response.data.message);
        setSelectedUsers([]);
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      showToast('操作失败', 'error');
    }
  };

  const handleBatchDisable = async () => {
    if (!window.confirm(`确定要禁用 ${selectedUsers.length} 个用户吗？`)) {
      return;
    }
    try {
      const response = await axios.put('/api/admin/users/batch-status', {
        ids: selectedUsers,
        isActive: false
      });
      if (response.data.success) {
        showToast(response.data.message);
        setSelectedUsers([]);
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      showToast('操作失败', 'error');
    }
  };

  const handleBatchDelete = async () => {
    if (!window.confirm(`确定要删除 ${selectedUsers.length} 个用户吗？此操作不可恢复！`)) {
      return;
    }
    try {
      const response = await axios.post('/api/admin/users/batch-delete', {
        ids: selectedUsers
      });
      if (response.data.success) {
        showToast(response.data.message);
        setSelectedUsers([]);
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      showToast('操作失败', 'error');
    }
  };

  const isOnline = (lastLoginAt) => {
    if (!lastLoginAt) return false;
    const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
    return new Date(lastLoginAt).getTime() > thirtyMinutesAgo;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  return (
    <AdminLayout title="用户管理">
      <Container>
        <Header>
          <Title>用户管理</Title>
          <HeaderActions>
            <Button onClick={() => { fetchUsers(); fetchStats(); }}>刷新</Button>
          </HeaderActions>
        </Header>

      {stats && (
        <StatsContainer>
          <StatCard>
            <StatValue color="#007bff">{stats.total}</StatValue>
            <StatLabel>总用户数</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#28a745">{stats.online}</StatValue>
            <StatLabel>在线用户</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#17a2b8">{stats.active}</StatValue>
            <StatLabel>活跃用户</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#6c757d">{stats.inactive}</StatValue>
            <StatLabel>禁用用户</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#dc3545">{stats.admin}</StatValue>
            <StatLabel>管理员</StatLabel>
          </StatCard>
        </StatsContainer>
      )}

      <FilterContainer>
        <SearchInput
          type="text"
          placeholder="搜索用户名、昵称、邮箱或手机号"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <FilterSelect value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
          <option value="">全部角色</option>
          <option value="user">普通用户</option>
          <option value="admin">管理员</option>
        </FilterSelect>
        <FilterSelect value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">全部状态</option>
          <option value="active">启用</option>
          <option value="inactive">禁用</option>
        </FilterSelect>
      </FilterContainer>

      {selectedUsers.length > 0 && (
        <BatchActions>
          <SelectedCount>已选择 {selectedUsers.length} 个用户</SelectedCount>
          <ActionButton className="success" onClick={handleBatchEnable}>批量启用</ActionButton>
          <ActionButton className="warning" onClick={handleBatchDisable}>批量禁用</ActionButton>
          <ActionButton className="danger" onClick={handleBatchDelete}>批量删除</ActionButton>
          <ActionButton onClick={() => setSelectedUsers([])}>取消选择</ActionButton>
        </BatchActions>
      )}

      {loading ? (
        <LoadingState>加载中...</LoadingState>
      ) : error ? (
        <ErrorState>{error}</ErrorState>
      ) : users.length === 0 ? (
        <EmptyState>暂无用户数据</EmptyState>
      ) : (
        <>
          <UserTable>
            <TableHeader>
              <div>
                <Checkbox
                  type="checkbox"
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onChange={handleSelectAll}
                />
              </div>
              <div>头像</div>
              <div>用户名</div>
              <div>昵称</div>
              <div>角色</div>
              <div>在线状态</div>
              <div>账户状态</div>
              <div>注册时间</div>
              <div>操作</div>
            </TableHeader>
            {users.map(user => (
              <UserRow key={user._id} selected={selectedUsers.includes(user._id)}>
                <div>
                  <Checkbox
                    type="checkbox"
                    checked={selectedUsers.includes(user._id)}
                    onChange={() => handleSelectUser(user._id)}
                  />
                </div>
                <Avatar>{getInitials(user.nickname || user.username)}</Avatar>
                <Username>{user.username}</Username>
                <Nickname>{user.nickname || '-'}</Nickname>
                <Role role={user.role}>{user.role === 'admin' ? '管理员' : '用户'}</Role>
                <OnlineStatus online={isOnline(user.lastLoginAt)}>
                  {isOnline(user.lastLoginAt) ? '在线' : '离线'}
                </OnlineStatus>
                <Status active={user.isActive}>
                  {user.isActive ? '启用' : '禁用'}
                </Status>
                <CreatedAt>{formatDate(user.createdAt)}</CreatedAt>
                <UserActions>
                  <ActionButton className="primary" onClick={() => handleViewDetail(user)}>详情</ActionButton>
                  <ActionButton className="primary" onClick={() => handleEdit(user)}>编辑</ActionButton>
                  <ActionButton
                    className={user.isActive ? 'warning' : 'success'}
                    onClick={() => handleToggleActive(user._id, user.isActive)}
                  >
                    {user.isActive ? '禁用' : '启用'}
                  </ActionButton>
                  <ActionButton className="danger" onClick={() => handleDelete(user._id)}>删除</ActionButton>
                </UserActions>
              </UserRow>
            ))}
          </UserTable>

          <Pagination>
            <PageButton
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              上一页
            </PageButton>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PageButton
                  key={pageNum}
                  className={pageNum === page ? 'active' : ''}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </PageButton>
              );
            })}
            <PageButton
              disabled={page === totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              下一页
            </PageButton>
            <PageInfo>第 {page} / {totalPages} 页</PageInfo>
          </Pagination>
        </>
      )}

      {detailModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>用户详情</ModalTitle>
              <CloseButton onClick={() => setDetailModal(null)}>×</CloseButton>
            </ModalHeader>
            <DetailInfo>
              <DetailLabel>用户ID:</DetailLabel>
              <DetailValue>{detailModal._id}</DetailValue>
              <DetailLabel>用户名:</DetailLabel>
              <DetailValue>{detailModal.username}</DetailValue>
              <DetailLabel>昵称:</DetailLabel>
              <DetailValue>{detailModal.nickname || '-'}</DetailValue>
              <DetailLabel>邮箱:</DetailLabel>
              <DetailValue>{detailModal.email || '-'}</DetailValue>
              <DetailLabel>手机号:</DetailLabel>
              <DetailValue>{detailModal.phone || '-'}</DetailValue>
              <DetailLabel>角色:</DetailLabel>
              <DetailValue>{detailModal.role === 'admin' ? '管理员' : '普通用户'}</DetailValue>
              <DetailLabel>账户状态:</DetailLabel>
              <DetailValue>{detailModal.isActive ? '启用' : '禁用'}</DetailValue>
              <DetailLabel>在线状态:</DetailLabel>
              <DetailValue>{isOnline(detailModal.lastLoginAt) ? '在线' : '离线'}</DetailValue>
              <DetailLabel>简介:</DetailLabel>
              <DetailValue>{detailModal.bio || '-'}</DetailValue>
              <DetailLabel>地址:</DetailLabel>
              <DetailValue>{detailModal.address || '-'}</DetailValue>
              <DetailLabel>注册时间:</DetailLabel>
              <DetailValue>{formatDate(detailModal.createdAt)}</DetailValue>
              <DetailLabel>最后登录:</DetailLabel>
              <DetailValue>{formatDate(detailModal.lastLoginAt)}</DetailValue>
              <DetailLabel>更新时间:</DetailLabel>
              <DetailValue>{formatDate(detailModal.updatedAt)}</DetailValue>
            </DetailInfo>
            <FormActions>
              <Button onClick={() => setDetailModal(null)}>关闭</Button>
              <Button primary onClick={() => { setDetailModal(null); handleEdit(detailModal); }}>编辑</Button>
            </FormActions>
          </ModalContent>
        </Modal>
      )}

      {editModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>编辑用户</ModalTitle>
              <CloseButton onClick={() => setEditModal(null)}>×</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleEditSubmit}>
              <FormGroup>
                <Label>用户名 *</Label>
                <Input
                  type="text"
                  value={editModal.username}
                  onChange={(e) => setEditModal({ ...editModal, username: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>昵称</Label>
                <Input
                  type="text"
                  value={editModal.nickname || ''}
                  onChange={(e) => setEditModal({ ...editModal, nickname: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>邮箱</Label>
                <Input
                  type="email"
                  value={editModal.email || ''}
                  onChange={(e) => setEditModal({ ...editModal, email: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>手机号</Label>
                <Input
                  type="text"
                  value={editModal.phone || ''}
                  onChange={(e) => setEditModal({ ...editModal, phone: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>角色</Label>
                <Select
                  value={editModal.role}
                  onChange={(e) => setEditModal({ ...editModal, role: e.target.value })}
                >
                  <option value="user">普通用户</option>
                  <option value="admin">管理员</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>账户状态</Label>
                <Select
                  value={editModal.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setEditModal({ ...editModal, isActive: e.target.value === 'active' })}
                >
                  <option value="active">启用</option>
                  <option value="inactive">禁用</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>简介</Label>
                <Textarea
                  value={editModal.bio || ''}
                  onChange={(e) => setEditModal({ ...editModal, bio: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>地址</Label>
                <Input
                  type="text"
                  value={editModal.address || ''}
                  onChange={(e) => setEditModal({ ...editModal, address: e.target.value })}
                />
              </FormGroup>
              <FormActions>
                <Button type="button" onClick={() => setEditModal(null)}>取消</Button>
                <Button type="submit" primary>保存</Button>
              </FormActions>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {toast && <Toast type={toast.type}>{toast.message}</Toast>}
    </Container>
    </AdminLayout>
  );
};

export default UsersPage;